import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

// The shared per-lead working notepad (`leads.leadWorkNotes`, migration 156).
//
// One free-text pad per lead (Sales' notes), so every surface that renders it
// must agree on the same rules. Extracted from the board slide-over's local
// `Notepad` (2026-09-04). Lead Handoffs and the Follow-ups panel, which
// also wrote it, are retired (client-intake-overhaul).
//
// The product contract (doc/guides/operations/onboarding-support.md):
//   - EXPLICIT save only. There is no autosave, ever. The Save button appears
//     only when the pad is dirty, and Cmd/Ctrl+Enter saves.
//   - Grow-only sizing: opens fitted to its content, grows as you type, never
//     auto-shrinks. Only the user's own drag on the resize grip shrinks it.
//   - Switching leads resets the pad to that lead's saved text.
//   - A dirty pad is NEVER clobbered by a background refetch.
//
// Transport is INJECTED (`onSave`), never owned here, and each caller
// invalidates its own query keys. Today's caller is the board slide-over, on
// `PATCH /onboarding-board/leads/:leadId/work-notes`. The injection also
// keeps this component testable with a plain
// jest.fn().

// Mirrors the server cap in onboardingBoardController.saveLeadWorkNotes, so a
// long paste is stopped at the keyboard instead of coming back as a 400.
export const MAX_WORK_NOTES = 20000;

const DEFAULT_MIN_HEIGHT = 76;

/**
 * Draft state for the pad: baseline tracking, dirty, and the adoption guard.
 *
 * Deliberately separate from the component so the draft can live in the ROW
 * that owns the pad. The Follow-ups panel unmounts the pad when its expansion
 * collapses; if the draft lived inside the component, collapsing would eat an
 * unsaved note.
 *
 * @param serverNotes the saved text from the server
 * @param resetKey    changes when the caller swaps to a different lead
 */
export const useLeadWorkNotesDraft = (serverNotes, resetKey) => {
  const server = serverNotes || '';
  const [value, setValue] = useState(server);
  const [baseline, setBaseline] = useState(server);
  const dirty = value !== baseline;

  // Switching leads (or reopening) resets the pad to that lead's saved text.
  useEffect(() => {
    setValue(server);
    setBaseline(server);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Fresh server text adopts only into a CLEAN pad; in-progress typing wins.
  //
  // ⚠️ Keyed on the STRING, never on the row/lead OBJECT. Board queries poll
  // (Follow-ups every 30s), so the containing object is a NEW reference on every
  // poll, and an effect keyed on it would fire constantly and wipe the draft.
  // Keyed
  // on the string this is a no-op unless someone genuinely saved different text,
  // which for a pad two teams share is exactly the case worth handling.
  useEffect(() => {
    setValue((v) => (v === baseline ? server : v));
    setBaseline(server);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server]);

  // Sets the BASELINE only, never `value`: keystrokes typed while the save was
  // in flight must stay dirty rather than be clobbered by the response.
  // Rejections propagate so the caller can toast and the pad stays dirty.
  const save = async (persist) => {
    const sent = value;
    const res = await persist(sent);
    setBaseline(sent);
    return res;
  };

  return { value, setValue, baseline, dirty, save };
};

/**
 * Drafts for MANY leads at once, owned by the surface that lists them.
 *
 * Use this instead of `useLeadWorkNotesDraft` wherever the component holding a
 * pad can unmount while the note is still unsaved. In the Follow-ups panel the
 * row unmounts whenever it is filtered out (a search, or a manager's owner
 * spotlight), which took the draft with it; holding drafts at the panel means
 * the note outlives its row.
 *
 * An ABSENT key means clean, so the pad mirrors the server. There is no
 * adoption effect and no baseline that can drift: a dirty lead reads its
 * draft, a clean one reads the server. A poll therefore cannot clobber typing
 * by construction rather than by guard.
 *
 * After a save the entry is deliberately KEPT (it equals the text just sent),
 * so the pad does not flash the stale server text while the refetch is in
 * flight. It goes clean on its own once the server catches up.
 */
export const useLeadWorkNotesDrafts = () => {
  const [drafts, setDrafts] = useState({});
  // Text we know landed on the server, per lead. Without this a pad reads
  // dirty for the moment between a successful save and the refetch that
  // brings the new text back, so a save that worked would still say "Unsaved
  // changes". Dropping the draft instead would flash the STALE note.
  const [saved, setSaved] = useState({});

  const setDraft = (leadId, text) =>
    setDrafts((d) => ({ ...d, [leadId]: text }));

  const markSaved = (leadId, text) =>
    setSaved((m) => ({ ...m, [leadId]: text }));

  const stateFor = (leadId, serverNotes) => {
    const server = serverNotes || '';
    const draft = Object.prototype.hasOwnProperty.call(drafts, leadId)
      ? drafts[leadId]
      : undefined;
    return {
      value: draft === undefined ? server : draft,
      // Clean when the draft matches either what the server holds or what we
      // last successfully sent. A later teammate save changes `server` but not
      // `saved`, so an in-progress draft still correctly reads dirty.
      dirty:
        draft !== undefined && draft !== server && draft !== saved[leadId],
    };
  };

  // How many of `leads` hold unsaved text. Callers must pass the UNFILTERED
  // list: the whole point is that a hidden row's draft still counts.
  const dirtyCount = (leads = []) =>
    leads.reduce(
      (n, l) => (stateFor(l.leadId, l.leadWorkNotes).dirty ? n + 1 : n),
      0,
    );

  return { setDraft, markSaved, stateFor, dirtyCount };
};

const FIELD_BASE =
  'block w-full resize-y text-sm leading-relaxed text-slate-700 outline-none placeholder:text-slate-300 disabled:opacity-60 dark:text-slate-200 dark:placeholder:text-slate-600';

// `card` is the board slide-over's look, where the field IS the card so the
// native resize grip sits at its true bottom-right corner. `inset` is a
// bordered field that sits inside a caller-owned card without double-shadowing.
const FIELD_VARIANTS = {
  card: `${FIELD_BASE} rounded-xl border-0 bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] focus:ring-2 focus:ring-slate-200 dark:!bg-surface-card dark:ring-1 dark:ring-white/10 dark:focus:ring-slate-500`,
  inset: `${FIELD_BASE} rounded-lg border border-slate-200 bg-white px-2.5 py-2 focus:border-slate-300 dark:border-white/10 dark:!bg-surface-card dark:focus:border-white/20`,
};

const savedCaptionOf = (updatedAt, updatedByLabel) => {
  if (!updatedAt) return null;
  const when = new Date(updatedAt).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Saved ${when}${updatedByLabel ? ` by ${updatedByLabel}` : ''}`;
};

const LeadWorkNotesPad = ({
  value,
  onChange,
  dirty,
  onSave,
  updatedAt = null,
  // Pre-resolved by the caller: 'you', a teammate's name, or null. Callers know
  // whether they have a name (Handoffs/Moved On resolve one server-side) or only
  // an id to compare against the viewer (board, Follow-ups).
  updatedByLabel = null,
  readOnly = false,
  busy = false,
  variant = 'inset',
  minHeight = DEFAULT_MIN_HEIGHT,
  placeholder = 'Notes for whoever works this lead…',
  autoFocus = false,
  className = '',
}) => {
  const taRef = useRef(null);
  const savedTimer = useRef(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const fitToContent = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight + 2, minHeight)}px`;
  };
  const growIfNeeded = () => {
    const el = taRef.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight) {
      el.style.height = `${el.scrollHeight + 2}px`;
    }
  };

  // Fit on mount, UNCONDITIONALLY: the pad must open showing the whole note.
  // This cannot share the focus guard below, because `autoFocus` lands during
  // commit (before effects run), so on an autofocusing pad the guard would see
  // its own textarea focused and skip the one fit that matters.
  useEffect(() => {
    fitToContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `autoFocus` focuses without positioning the caret, so it sits at index 0
  // and new text gets typed in FRONT of an existing note. Put it at the end,
  // which is where clicking into the field would have left it.
  useEffect(() => {
    if (!autoFocus) return;
    const el = taRef.current;
    if (el) el.setSelectionRange(el.value.length, el.value.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refit when the text changes from OUTSIDE the field (a lead switch, or an
  // adopted teammate save). Typing keeps focus, so it grows via growIfNeeded
  // instead and is never shrunk out from under the user mid-note.
  useEffect(() => {
    if (document.activeElement !== taRef.current) {
      requestAnimationFrame(fitToContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, minHeight]);

  // The pad unmounts when the Follow-ups expansion collapses, so this timer can
  // outlive it.
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const doSave = async () => {
    if (!dirty || saving || readOnly) return;
    setSaving(true);
    try {
      await onSave();
      setJustSaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setJustSaved(false), 2000);
    } catch {
      // The caller owns the error toast. Swallow here so the pad stays dirty
      // and the unsaved text is never thrown away.
    } finally {
      setSaving(false);
    }
  };

  const caption = savedCaptionOf(updatedAt, updatedByLabel);
  const showSave = dirty && !readOnly;

  return (
    <div className={className}>
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          growIfNeeded();
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') doSave();
        }}
        disabled={readOnly}
        maxLength={MAX_WORK_NOTES}
        placeholder={placeholder}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        style={{ minHeight }}
        className={FIELD_VARIANTS[variant] || FIELD_VARIANTS.inset}
      />
      <div className="flex min-h-[26px] items-center justify-between gap-2 pt-1.5">
        <span className="text-[11px]">
          {dirty ? (
            <span className="font-medium text-amber-600 dark:text-amber-400">
              Unsaved changes
            </span>
          ) : justSaved ? (
            <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
              <Check size={12} /> Saved
            </span>
          ) : caption ? (
            <span className="text-slate-400 dark:text-slate-500">{caption}</span>
          ) : null}
        </span>
        {showSave ? (
          <button
            type="button"
            onClick={doSave}
            disabled={saving || busy}
            className="rounded-md bg-slate-900 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-60 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default LeadWorkNotesPad;
